import React, { useCallback, useEffect, useState } from 'react';
import './LecturesPage.css';
import { markLectureSeen } from '../../services/lectureViewingState';
import useDraggablePanel from '../../hooks/useDraggablePanel';

export const LECTURES = [
  {
    id: 'simple-reflections',
    title: 'Simple Reflections',
    embedUrl: 'https://www.youtube.com/embed/bVTEFhq5mAs',
    thumbnailUrl: '/decoded-assets/simpleReflectionLectureWallpaperLight.webp',
  },
  {
    id: 'complex-reflections',
    title: 'Complex Reflections',
    embedUrl: 'https://www.youtube.com/embed/PZtp-UOSTPY',
    thumbnailUrl: '/decoded-assets/complexReflectionsLectureWallpaperLight.webp',
  },
  {
    id: 'specific-skills',
    title: 'Specific Skills',
    embedUrl: 'https://www.youtube.com/embed/NYiqMUpmm-Q',
    thumbnailUrl: '/decoded-assets/specificSkillsLectureWallpaperLight.webp',
  },
  {
    id: 'full-session',
    title: 'Full Session',
    embedUrl: 'https://www.youtube.com/embed/0gDKySKZ1F0',
    thumbnailUrl: '/decoded-assets/fullSessionLectureWallpaperLight.webp',
  },
];

export default function LecturesPage({ onBack, activeLectureId = null, onOpenLecture, onCloseLecture }) {
  const [activeVideo, setActiveVideo] = useState(null);
  const lectureDrag = useDraggablePanel(Boolean(activeVideo));

  useEffect(() => {
    document.body.classList.add('anonymous-landing-page');
    document.documentElement.classList.add('anonymous-landing-page');
    return () => {
      document.body.classList.remove('anonymous-landing-page');
      document.documentElement.classList.remove('anonymous-landing-page');
    };
  }, []);

  useEffect(() => {
    if (!activeLectureId) {
      setActiveVideo(null);
      return;
    }
    const matchingLecture = LECTURES.find((lecture) => lecture.id === activeLectureId) || null;
    if (matchingLecture) {
      markLectureSeen(matchingLecture.id);
    }
    setActiveVideo(matchingLecture);
  }, [activeLectureId]);

  const handleOpenVideo = useCallback((lecture) => {
    markLectureSeen(lecture.id);
    setActiveVideo(lecture);
    if (onOpenLecture) {
      onOpenLecture(lecture.id);
    }
  }, [onOpenLecture]);

  const handleCloseVideo = useCallback(() => {
    setActiveVideo(null);
    if (onCloseLecture) {
      onCloseLecture();
    }
  }, [onCloseLecture]);

  useEffect(() => {
    if (!activeVideo) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCloseVideo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeVideo, handleCloseVideo]);

  return (
    <div className="lectures-page">
      <header className="lectures-page__header">
        <button type="button" className="lectures-page__back" onClick={onBack}>
          <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
            <path d="M12.5 4L7 10l5.5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
          Back
        </button>
        <div className="lectures-page__brand">AIMIE</div>
      </header>

      <main className="lectures-page__main">
        <div className="lectures-page__hero">
          <h1 className="lectures-page__title">Lecture Videos</h1>
          <p className="lectures-page__eyebrow">Accompanying lectures as you interact with our clients and improve your MI skills</p>
        </div>

        <div className="lectures-page__grid">
          {LECTURES.map((lecture) => (
            <button
              key={lecture.id}
              type="button"
              className="lectures-page__card"
              onClick={() => handleOpenVideo(lecture)}
            >
              <div className="lectures-page__thumbnail">
                <img src={lecture.thumbnailUrl} alt={`${lecture.title} thumbnail`} />
                <span className="lectures-page__play" aria-hidden="true">
                  <svg viewBox="0 0 48 48">
                    <circle cx="24" cy="24" r="22" fill="rgba(0,0,0,0.45)" />
                    <path d="M19 14l18 10-18 10z" fill="#f7f4ec" />
                  </svg>
                </span>
              </div>
            </button>
          ))}
        </div>
      </main>

      {activeVideo && (
        <div
          className="lectures-page__modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lectures-modal-title"
          onClick={handleCloseVideo}
        >
          <div
            className="lectures-page__modal"
            ref={lectureDrag.panelRef}
            style={lectureDrag.panelStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`lectures-page__modal-header${lectureDrag.isDragging ? ' is-dragging' : ''}`}
              {...lectureDrag.dragHandleProps}
            >
              <h3 id="lectures-modal-title">{activeVideo.title}</h3>
              <button
                type="button"
                className="lectures-page__modal-close"
                onClick={handleCloseVideo}
                aria-label="Close video"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="lectures-page__modal-frame">
              <iframe
                src={`${activeVideo.embedUrl}?autoplay=1`}
                title={activeVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
